import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import InjuryBadge from "./InjuryBadge";
import { openPdf, pdfUrl } from "@/lib/api";
import { Download, Ambulance, AlertCircle } from "lucide-react";
import clsx from "clsx";

const OCCUPANT_COLUMNS = ["Veh", "Name", "Role", "Seat", "P12", "Area", "Hospital / EMS"];
const NON_MOTORIST_COLUMNS = ["No", "Name", "Type", "—", "P12", "Area", "Hospital / EMS"];
const INSURANCE_COLUMNS = ["Party", "Driver / Owner", "Carrier", "Policy #", "Status"];

function handleDownload(report) {
  const r = openPdf(report.id);
  if (r.method === "new-tab") toast.success("PDF opened in a new tab");
  else if (r.method === "same-tab") toast.success("Downloading…");
  else if (r.method === "clipboard") toast.message("PDF URL copied — paste it in a new browser tab.");
  else toast.error("Could not open PDF. Right-click the button and choose 'Open link in new tab'.");
}

export default function ReportDetailModal({ report, onClose }) {
  const open = !!report;
  const occupants = report?.people?.filter((p) => p.category !== "non_motorist") || [];
  const nonMotorists = report?.people?.filter((p) => p.category === "non_motorist") || [];

  const renderRow = (p, i) => (
    <tr
      key={i}
      className={clsx(
        "border-b border-zinc-200 font-mono text-[12px] align-top",
        !p.qualifying && "opacity-50"
      )}
    >
      <td className="py-2 px-3">{p.vehicle_no || p.non_motorist_no || "—"}</td>
      <td className="py-2 px-3">{p.name || "—"}</td>
      <td className="py-2 px-3">
        {p.category === "non_motorist"
          ? p.non_motorist_type || "Non-Motorist"
          : p.person_type_label || p.person_type || "—"}
      </td>
      <td className="py-2 px-3">{p.seat || "—"}</td>
      <td className="py-2 px-3">
        <InjuryBadge code={p.injury_code} transported={p.transported} />
      </td>
      <td className="py-2 px-3 max-w-[160px] truncate">{p.injury_area || "—"}</td>
      <td className="py-2 px-3">
        {p.transported ? (
          <div className="flex items-start gap-1">
            <Ambulance className="w-3.5 h-3.5 mt-[2px] shrink-0" />
            <div className="leading-tight">
              {p.medical_facility_name && (
                <div className="truncate max-w-[240px]">{p.medical_facility_name}</div>
              )}
              {p.ems_service_name && (
                <div className="truncate max-w-[240px] opacity-70">{p.ems_service_name}</div>
              )}
            </div>
          </div>
        ) : (
          <span className="opacity-50">—</span>
        )}
      </td>
    </tr>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="report-detail-modal"
        className="max-w-5xl border-2 border-black rounded-none p-0 bg-white"
      >
        <DialogHeader className="border-b-2 border-black p-5">
          <DialogTitle className="font-chivo text-2xl font-black tracking-tighter">
            {report?.file_name}
          </DialogTitle>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 font-mono text-[11px] uppercase tracking-[0.2em] opacity-70">
            {report?.case_no && <span>Case: {report.case_no}</span>}
            {report?.crash_date && <span>Date: {report.crash_date}</span>}
            {report?.crash_location && <span>Loc: {report.crash_location}</span>}
          </div>
        </DialogHeader>

        <div className="p-5 space-y-6 max-h-[65vh] overflow-y-auto">
          {occupants.length > 0 && (
            <Section title={`Drivers & Occupants (${occupants.length})`}>
              <table className="w-full text-sm">
                <Head columns={OCCUPANT_COLUMNS} />
                <tbody>{occupants.map(renderRow)}</tbody>
              </table>
            </Section>
          )}
          {nonMotorists.length > 0 && (
            <Section title={`Non-Motorists (${nonMotorists.length})`}>
              <table className="w-full text-sm">
                <Head columns={NON_MOTORIST_COLUMNS} />
                <tbody>{nonMotorists.map(renderRow)}</tbody>
              </table>
            </Section>
          )}
          {report && (report.insurance_info || []).length === 0 && (
            <Section title="Insurance">
              <div className="p-4" data-testid="modal-no-insurance-flag">
                <span className="inline-flex items-center gap-1.5 bg-[#C1121F] text-white border border-black px-2 py-1 font-mono text-[11px] uppercase tracking-[0.18em] font-bold">
                  <AlertCircle className="w-3.5 h-3.5" /> No Insurance — Review
                </span>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.12em] text-red-700 font-bold">
                  No carrier found — check the original PDF for an insurance block.
                </p>
              </div>
            </Section>
          )}
          {(report?.insurance_info || []).length > 0 && (
            <Section title={`Insurance (${report.insurance_info.length})`}>
              <table className="w-full text-sm" data-testid="modal-insurance-table">
                <Head columns={INSURANCE_COLUMNS} />
                <tbody>
                  {report.insurance_info.map((ins, i) => (
                    <tr key={i} className="border-b border-zinc-200 font-mono text-[12px] align-top">
                      <td className="py-2 px-3 font-bold">{ins.party || `V${i + 1}`}</td>
                      <td className="py-2 px-3">{ins.driver_name || ins.owner_name || "—"}</td>
                      <td className="py-2 px-3">{ins.carrier || "—"}</td>
                      <td className="py-2 px-3">{ins.policy_no || "—"}</td>
                      <td className="py-2 px-3">
                        {ins.status ? (
                          <span
                            className={clsx(
                              "border px-1.5 py-[1px] text-[10px] uppercase tracking-[0.15em] font-bold",
                              ins.status.toLowerCase() === "uninsured"
                                ? "border-red-600 text-red-700 bg-red-50"
                                : ins.status.toLowerCase() === "insured"
                                ? "border-black text-black bg-zinc-100"
                                : "border-zinc-400 text-zinc-500"
                            )}
                          >
                            {ins.status}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </div>

        <div className="border-t-2 border-black p-4 flex justify-end gap-2">
          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="border border-black bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-100"
          >
            Close
          </button>
          {report && (
            <a
              data-testid="modal-download-btn"
              href={pdfUrl(report.id)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                handleDownload(report);
              }}
              className="inline-flex items-center gap-1 border border-black bg-black text-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-800 no-underline cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" /> Download PDF
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-black">
      <div className="border-b-2 border-black bg-black text-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em]">
        {title}
      </div>
      {children}
    </div>
  );
}

function Head({ columns }) {
  return (
    <thead>
      <tr className="border-b-2 border-black bg-zinc-50 text-left font-mono text-[11px] uppercase tracking-[0.2em]">
        {columns.map((c) => (
          <th key={c} className="py-2 px-3">
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}
